import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/storefront/ProductCard";
import SEO from "@/components/SEO";
import {
    breadcrumbJsonLd,
    brandSeoDescription,
    brandSeoTitle,
    categorySeoDescription,
    categorySeoTitle,
    STORE_NAME,
} from "@/lib/seo";

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const q = params.get("q") || "";
    const cat = params.get("category") || "";
    const brand = params.get("brand") || "";
    const newOnly = params.get("new") === "1";
    const saleOnly = params.get("sale") === "1";
    const featured = params.get("featured") === "1";
    const sort = params.get("sort") || "newest";

    useEffect(() => {
        (async () => {
            try {
                const [c, b] = await Promise.all([
                    api.get("/store/categories"),
                    api.get("/store/brands"),
                ]);
                setCategories(c.data || []);
                setBrands(b.data || []);
            } catch (_) {}
        })();
    }, []);

    useEffect(() => {
        setLoading(true);
        const p = {};
        if (q) p.q = q;
        if (cat) p.category = cat;
        if (brand) p.brand = brand;
        if (newOnly) p.new_arrival = true;
        if (saleOnly) p.on_sale = true;
        if (featured) p.featured = true;
        if (sort) p.sort = sort;
        api.get("/store/products", { params: p })
            .then((r) => setProducts(r.data || []))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [q, cat, brand, newOnly, saleOnly, featured, sort]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value || value === "all") next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const heading = useMemo(() => {
        if (saleOnly) return "Sale";
        if (newOnly) return "New arrivals";
        if (featured) return "Best sellers";
        if (q) return `Results for "${q}"`;
        if (cat) return categories.find((c) => c.slug === cat)?.name || "Category";
        if (brand) return brands.find((b) => b.slug === brand)?.name || "Brand";
        return "All products";
    }, [saleOnly, newOnly, featured, q, cat, brand, categories, brands]);

    // SEO computed values
    const seo = useMemo(() => {
        // Detect "filter-noise" params that create duplicate variants of an
        // otherwise-canonical /shop or /shop?category=… / /shop?brand=… view.
        // Their presence forces noindex AND collapses canonical to the clean
        // parent URL so Google de-duplicates against the indexable parent.
        const hasSort = !!params.get("sort");
        const hasPage = !!params.get("page");
        const hasMultiFilter = !!cat && !!brand;
        const hasBooleanFilter = newOnly || saleOnly || featured;
        const isDuplicateView = hasSort || hasPage || hasMultiFilter || hasBooleanFilter;

        // Per-category page (only when it's a CLEAN category view — no other filter noise)
        if (cat && !isDuplicateView) {
            const name = categories.find((c) => c.slug === cat)?.name;
            if (name) {
                return {
                    title: categorySeoTitle(name),
                    description: categorySeoDescription(name),
                    path: `/shop?category=${cat}`,
                    crumbs: [
                        { name: "Home", path: "/" },
                        { name: "Shop", path: "/shop" },
                        { name: name, path: `/shop?category=${cat}` },
                    ],
                    noindex: false,
                };
            }
        }
        // Per-brand page (only when CLEAN — no other filter noise)
        if (brand && !isDuplicateView) {
            const name = brands.find((b) => b.slug === brand)?.name;
            if (name) {
                return {
                    title: brandSeoTitle(name),
                    description: brandSeoDescription(name),
                    path: `/shop?brand=${brand}`,
                    crumbs: [
                        { name: "Home", path: "/" },
                        { name: "Shop", path: "/shop" },
                        { name: name, path: `/shop?brand=${brand}` },
                    ],
                    noindex: false,
                };
            }
        }
        // Search results — always noindex, canonical to /shop.
        if (q) {
            return {
                title: `Search results for "${q}" | ${STORE_NAME}`,
                description: `Search results for "${q}" — fishing tackle, rods, reels and lures from ${STORE_NAME}.`,
                path: `/shop`,
                crumbs: [
                    { name: "Home", path: "/" },
                    { name: "Shop", path: "/shop" },
                ],
                noindex: true, // avoid index bloat
            };
        }
        // Filter-noise views: noindex, canonical collapses to clean parent.
        // Order: category > brand > /shop. Multi-filter category+brand
        // canonicalises to category (more useful indexable target).
        if (isDuplicateView) {
            let canonicalPath = "/shop";
            if (cat) canonicalPath = `/shop?category=${cat}`;
            else if (brand) canonicalPath = `/shop?brand=${brand}`;
            return {
                title: `Shop All Fishing Tackle Online in India | ${STORE_NAME}`,
                description: `Shop the full range of fishing rods, reels, lures, lines and accessories online from ${STORE_NAME}. Authentic gear, pan-India shipping.`,
                path: canonicalPath,
                crumbs: [
                    { name: "Home", path: "/" },
                    { name: "Shop", path: "/shop" },
                ],
                noindex: true,
            };
        }
        // Default /shop
        return {
            title: `Shop All Fishing Tackle Online in India | ${STORE_NAME}`,
            description: `Shop the full range of fishing rods, reels, lures, lines and accessories online from ${STORE_NAME}. Authentic gear, pan-India shipping.`,
            path: "/shop",
            crumbs: [
                { name: "Home", path: "/" },
                { name: "Shop", path: "/shop" },
            ],
            noindex: false,
        };
    }, [cat, brand, q, categories, brands, newOnly, saleOnly, featured, params]);

    return (
        <div className="container mx-auto px-4 py-6 md:py-10" data-testid="shop-page">
            <SEO
                title={seo.title}
                description={seo.description}
                path={seo.path}
                noindex={seo.noindex}
                jsonLd={breadcrumbJsonLd(seo.crumbs)}
            />
            <div className="mb-6 md:mb-8">
                <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Shop</div>
                <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight text-white mt-1">
                    {heading}
                </h1>
                {/* SEO intro — only on the default /shop view (no active filter).
                    Gives Googlebot static, topical content so /shop is never classified
                    as Soft 404. Hidden when the user has picked a category, brand,
                    sale, new-arrivals, search, or featured filter. */}
                {!cat && !brand && !saleOnly && !newOnly && !featured && !q && (
                    <p className="mt-4 max-w-2xl text-sm md:text-base text-white/65 leading-relaxed" data-testid="shop-seo-intro">
                        Browse the full {STORE_NAME} catalogue — fishing rods, spinning and casting
                        reels, soft and hard lures, braided and monofilament lines, hooks, terminal
                        tackle and accessories handpicked for Indian anglers. Whether you fish
                        saltwater off Mumbai's rocky shoreline, freshwater reservoirs in Karnataka,
                        or the rivers of the North-East, our gear is sourced from trusted global
                        brands and shipped pan-India. Use the filters below to shop by category,
                        brand, or look for what's on sale and newly arrived.
                    </p>
                )}
            </div>

            <div className="flex items-center gap-3 mb-5 md:mb-7">
                <button
                    className="md:hidden inline-flex items-center gap-2 px-3.5 py-2 border border-[#27272A] rounded-sm text-white text-sm font-display uppercase tracking-widest"
                    onClick={() => setFiltersOpen(true)}
                    data-testid="shop-open-filters-mobile"
                >
                    <SlidersHorizontal size={14} /> Filters
                </button>
                <select
                    value={sort}
                    onChange={(e) => setParam("sort", e.target.value)}
                    className="ml-auto bg-[#121214] border border-[#27272A] rounded-sm py-2 px-3 text-white text-sm focus:border-[#00BCE5] outline-none"
                    data-testid="shop-sort-select"
                >
                    <option value="newest">Newest</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="name">Name</option>
                </select>
            </div>

            <div className="grid md:grid-cols-[260px,1fr] gap-6 md:gap-8">
                {/* Desktop filters */}
                <aside className="hidden md:block">
                    <FilterPanel
                        categories={categories}
                        brands={brands}
                        cat={cat}
                        brand={brand}
                        saleOnly={saleOnly}
                        newOnly={newOnly}
                        setParam={setParam}
                    />
                </aside>

                <div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="aspect-[3/4] bg-[#121214] border border-[#27272A] animate-pulse rounded-sm" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="border border-dashed border-[#27272A] rounded-sm p-10 text-center text-white/55">
                            No products match your filters.{" "}
                            <Link to="/shop" className="text-[#00BCE5] underline">
                                Clear filters
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} testIdPrefix="shop-card" />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile filters drawer */}
            {filtersOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setFiltersOpen(false)} />
                    <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#0A0A0B] border-l border-[#27272A] p-5 overflow-y-auto">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="font-display uppercase font-black text-xl">Filters</h3>
                            <button onClick={() => setFiltersOpen(false)} className="text-white" data-testid="shop-close-filters-mobile">
                                <X size={22} />
                            </button>
                        </div>
                        <FilterPanel
                            categories={categories}
                            brands={brands}
                            cat={cat}
                            brand={brand}
                            saleOnly={saleOnly}
                            newOnly={newOnly}
                            setParam={(k, v) => {
                                setParam(k, v);
                            }}
                        />
                        <button
                            onClick={() => setFiltersOpen(false)}
                            className="mt-6 w-full bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-3 rounded-sm"
                            data-testid="shop-apply-filters-mobile"
                        >
                            Show results
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterPanel({ categories, brands, cat, brand, saleOnly, newOnly, setParam }) {
    return (
        <div className="space-y-6">
            <div>
                <h4 className="font-display text-xs uppercase tracking-[0.25em] text-white/55 mb-2">Quick</h4>
                <div className="flex flex-col gap-1">
                    <button
                        className={`text-left py-2 px-3 rounded-sm border ${saleOnly ? "border-[#00BCE5] text-[#00BCE5]" : "border-[#27272A] text-white"}`}
                        onClick={() => setParam("sale", saleOnly ? "" : "1")}
                        data-testid="filter-sale-toggle"
                    >
                        Sale
                    </button>
                    <button
                        className={`text-left py-2 px-3 rounded-sm border ${newOnly ? "border-[#00BCE5] text-[#00BCE5]" : "border-[#27272A] text-white"}`}
                        onClick={() => setParam("new", newOnly ? "" : "1")}
                        data-testid="filter-new-toggle"
                    >
                        New Arrivals
                    </button>
                </div>
            </div>
            <div>
                <h4 className="font-display text-xs uppercase tracking-[0.25em] text-white/55 mb-2">Category</h4>
                <div className="flex flex-col">
                    <Link
                        to="/shop"
                        className={`text-left py-2 px-3 rounded-sm ${!cat ? "text-[#00BCE5]" : "text-white/75 hover:text-white"}`}
                        onClick={(e) => { e.preventDefault(); setParam("category", ""); }}
                    >
                        All categories
                    </Link>
                    {categories.map((c) => (
                        <Link
                            key={c.id}
                            to={`/shop?category=${c.slug}`}
                            className={`text-left py-2 px-3 rounded-sm ${cat === c.slug ? "text-[#00BCE5]" : "text-white/75 hover:text-white"}`}
                            onClick={(e) => { e.preventDefault(); setParam("category", c.slug); }}
                            data-testid={`filter-category-${c.slug}`}
                        >
                            {c.name}
                        </Link>
                    ))}
                </div>
            </div>
            <div>
                <h4 className="font-display text-xs uppercase tracking-[0.25em] text-white/55 mb-2">Brand</h4>
                <div className="flex flex-col">
                    <Link
                        to="/shop"
                        className={`text-left py-2 px-3 rounded-sm ${!brand ? "text-[#00BCE5]" : "text-white/75 hover:text-white"}`}
                        onClick={(e) => { e.preventDefault(); setParam("brand", ""); }}
                    >
                        All brands
                    </Link>
                    {brands.map((b) => (
                        <Link
                            key={b.id}
                            to={`/shop?brand=${b.slug}`}
                            className={`text-left py-2 px-3 rounded-sm ${brand === b.slug ? "text-[#00BCE5]" : "text-white/75 hover:text-white"}`}
                            onClick={(e) => { e.preventDefault(); setParam("brand", b.slug); }}
                            data-testid={`filter-brand-${b.slug}`}
                        >
                            {b.name}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
